import { FC, useState, useEffect, useCallback, useRef } from 'react';
import {
  Clock,
  Zap,
  RefreshCw,
  Send,
  AlertCircle,
  Users,
  CheckCircle2,
  Calendar,
  Trash2,
  ShieldCheck,
  Copy,
  Check,
  ExternalLink,
  Eye,
  AlertTriangle,
  RotateCcw,
  GripVertical,
  ChevronDown,
  ChevronUp,
  Plus,
  Layers,
  Pause,
  Play,
  ArrowUpToLine,
  Ticket,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../ui/Toast';
import { PageHeader } from '../widgets/PageHeader';
import { MetricWidget } from '../widgets/MetricWidget';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Drawer } from '../ui/Drawer';
import { Input } from '../ui/Input';
import { ConfirmationModal } from '../ui/ConfirmationModal';
import { DataTable } from '../widgets/DataTable';
import { Tooltip } from '../ui/Tooltip';
import { WhatsAppBubblePreview } from '../widgets/WhatsAppBubblePreview';
import { ApplyCouponDrawer } from '../coupons/ApplyCouponDrawer';
import { applyCouponToQueueItem } from '../../api/coupons';
import {
  fetchQueueItems,
  dispatchQueueItemNow,
  cancelQueueItem,
  fetchCadenceTelemetry,
  fetchQueueSettings,
  updateQueueSettings,
  pauseQueue,
  resumeQueue,
  batchQueueAction,
  reorderQueueItems,
  fetchDispatchQueues,
  createDispatchQueue,
  updateDispatchQueue,
  pauseDispatchQueue,
  resumeDispatchQueue,
  updateQueueItem,
  QueueItem,
  QueueCadenceTelemetry,
  QueueSettings,
  DispatchQueue,
  CreateDispatchQueuePayload,
  UpdateDispatchQueuePayload,
} from '../../api/queue';
import { PacingDrawer } from './PacingDrawer';
import { fetchWhatsAppStatus, WhatsAppStatus } from '../../api/whatsapp';
import { fetchGroups, WhatsAppGroup } from '../../api/groups';

interface QueueViewProps {
  initialTelemetry?: QueueCadenceTelemetry | null;
  initialWhatsappStatus?: WhatsAppStatus | null;
  initialSelectedItem?: QueueItem | null;
  onNavigate?: (tab: string, offerId?: string) => void;
  onTelemetryChange?: (telemetry: QueueCadenceTelemetry) => void;
}

export const QueueView: FC<QueueViewProps> = ({
  initialTelemetry = null,
  initialWhatsappStatus = null,
  initialSelectedItem = null,
  onNavigate,
  onTelemetryChange,
}) => {
  const { token } = useAuth();
  const { success, error: showError } = useToast();

  const [items, setItems] = useState<QueueItem[]>([]);
  const [telemetry, setTelemetry] = useState<QueueCadenceTelemetry | null>(
    initialTelemetry
  );
  const [whatsappStatus, setWhatsappStatus] = useState<WhatsAppStatus | null>(
    initialWhatsappStatus
  );
  const [groups, setGroups] = useState<WhatsAppGroup[]>([]);
  const [isLoading, setIsLoading] = useState(
    !initialTelemetry && !initialWhatsappStatus
  );
  const [dispatchingId, setDispatchingId] = useState<string | null>(null);
  const [isRequeuingId, setIsRequeuingId] = useState<string | null>(null);
  const [isExecutingBatch, setIsExecutingBatch] = useState(false);
  const [itemToDispatchNow, setItemToDispatchNow] = useState<QueueItem | null>(
    null
  );
  const [couponTargetQueueItem, setCouponTargetQueueItem] =
    useState<QueueItem | null>(null);

  // Estado das Filas Segmentadas Multi-Queue
  const [dispatchQueues, setDispatchQueues] = useState<DispatchQueue[]>([]);
  const [selectedQueueId, setSelectedQueueId] = useState<string>('all');
  const [isNewQueueDrawerOpen, setIsNewQueueDrawerOpen] = useState(false);
  const [isSavingNewQueue, setIsSavingNewQueue] = useState(false);
  const [isTogglingQueuePause, setIsTogglingQueuePause] = useState(false);
  const [newQueueForm, setNewQueueForm] = useState<CreateDispatchQueuePayload>({
    name: '',
    slug: '',
    strategy_type: 'daily_deals',
    priority: 1,
    target_group_ids: [],
  });

  // Estado para remoção de item da fila
  const [itemToRemove, setItemToRemove] = useState<QueueItem | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Estado para Drawer de configurações da esteira
  const [isSettingsDrawerOpen, setIsSettingsDrawerOpen] = useState(false);
  const [isPacingDrawerOpen, setIsPacingDrawerOpen] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState<QueueSettings>({
    dispatch_start_hour: 8,
    dispatch_end_hour: 22,
    min_delay_minutes: 4,
    max_delay_minutes: 8,
    safe_capacity_daily: 150,
  });

  const onTelemetryChangeRef = useRef(onTelemetryChange);
  useEffect(() => {
    onTelemetryChangeRef.current = onTelemetryChange;
  }, [onTelemetryChange]);

  const selectedQueueIdRef = useRef(selectedQueueId);
  useEffect(() => {
    selectedQueueIdRef.current = selectedQueueId;
  }, [selectedQueueId]);

  const loadData = useCallback(
    async (activeQueueId?: string) => {
      if (!token) return;
      setIsLoading(true);
      setLoadError(null);
      try {
        const currentQueueId = activeQueueId ?? selectedQueueIdRef.current;
        const queueParam =
          currentQueueId === 'all' ? undefined : currentQueueId;
        const [queueData, cadenceData, waData, groupsData, queuesData] =
          await Promise.all([
            fetchQueueItems(token, undefined, queueParam),
            fetchCadenceTelemetry(token, queueParam),
            fetchWhatsAppStatus(token).catch(() => null),
            fetchGroups(token).catch(() => [] as WhatsAppGroup[]),
            fetchDispatchQueues(token).catch(() => [] as DispatchQueue[]),
          ]);
        setItems(queueData);
        setTelemetry(cadenceData);
        setWhatsappStatus(waData);
        setGroups(groupsData);
        setDispatchQueues(queuesData);
        setLoadError(null);
        onTelemetryChangeRef.current?.(cadenceData);
      } catch (err: unknown) {
        const message =
          err instanceof Error
            ? err.message
            : 'Falha ao carregar fila de disparos.';
        setLoadError(message);
        showError(message, 'Erro de Carregamento');
      } finally {
        setIsLoading(false);
      }
    },
    [token, showError]
  );

  const loadSettings = useCallback(async () => {
    if (!token) return;
    try {
      const data = await fetchQueueSettings(token);
      setSettingsForm(data);
    } catch {
      // Mantém valores padrão caso falhe
    }
  }, [token]);

  useEffect(() => {
    loadData();
    loadSettings();

    // Auto-refresh contínuo para manter os horários e telemetria em tempo real (15 segundos)
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadData();
      }
    }, 15000);

    const handleFocus = () => {
      loadData();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [loadData, loadSettings]);

  const isDispatchingAny = Boolean(
    dispatchingId || isRequeuingId || isExecutingBatch
  );

  const handleOpenDispatchConfirmation = (item: QueueItem) => {
    if (isDispatchingAny) return;
    setItemToDispatchNow(item);
  };

  const handleConfirmDispatchNow = async () => {
    if (!token || !itemToDispatchNow) return;
    const item = itemToDispatchNow;
    setDispatchingId(item.id);
    try {
      const updated = await dispatchQueueItemNow(token, item.id);
      if (updated.status === 'failed') {
        showError(
          updated.last_error || 'Falha ao entregar nos grupos do WhatsApp.',
          'Falha no Disparo'
        );
      } else {
        success(
          `Disparo imediato da oferta "${item.offer_title || 'Oferta'}" concluído com sucesso!`,
          'Disparo Concluído'
        );
      }
      setItemToDispatchNow(null);
      // Recarrega todos os horários recalculados e telemetria em tempo real
      await loadData();
      window.dispatchEvent(new CustomEvent('operational-health-refresh'));
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : 'Erro ao executar o envio imediato.';
      showError(message, 'Falha no Disparo');
    } finally {
      setDispatchingId(null);
    }
  };

  const handleRequeueSingleItem = async (item: QueueItem) => {
    if (!token || isDispatchingAny) return;
    setIsRequeuingId(item.id);
    try {
      const res = await batchQueueAction(token, 'requeue', [item.id]);
      success(res.message, 'Oferta Re-enfileirada');
      if (selectedQueueItem?.id === item.id) {
        setSelectedQueueItem(null);
      }
      await loadData();
      window.dispatchEvent(new CustomEvent('operational-health-refresh'));
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Erro ao re-enfileirar oferta.';
      showError(msg, 'Erro');
    } finally {
      setIsRequeuingId(null);
    }
  };

  // Estados para Detalhes da Oferta na Fila (Drawer Lateral)
  const [selectedQueueItem, setSelectedQueueItem] = useState<QueueItem | null>(
    initialSelectedItem
  );
  const [isCopyingError, setIsCopyingError] = useState(false);
  const [isTogglingPause, setIsTogglingPause] = useState(false);

  // Estados para Edição Manual e WhatsApp Live Preview do Item na Fila
  const [isSavingQueueItem, setIsSavingQueueItem] = useState(false);
  const [isSendingAsNextId, setIsSendingAsNextId] = useState<string | null>(
    null
  );
  const [editTitle, setEditTitle] = useState(
    initialSelectedItem?.custom_title_override ||
      initialSelectedItem?.offer_title ||
      ''
  );
  const [editCopy, setEditCopy] = useState(
    initialSelectedItem?.custom_copy_override ||
      initialSelectedItem?.offer_copy ||
      ''
  );
  const [editImageUrl, setEditImageUrl] = useState(
    initialSelectedItem?.custom_image_url_override ||
      initialSelectedItem?.offer_image_url ||
      ''
  );
  const [editCoupon, setEditCoupon] = useState(
    initialSelectedItem?.offer_coupon_code || ''
  );

  useEffect(() => {
    if (selectedQueueItem) {
      setEditTitle(
        selectedQueueItem.custom_title_override ||
          selectedQueueItem.offer_title ||
          ''
      );
      setEditCopy(
        selectedQueueItem.custom_copy_override ||
          selectedQueueItem.offer_copy ||
          ''
      );
      setEditImageUrl(
        selectedQueueItem.custom_image_url_override ||
          selectedQueueItem.offer_image_url ||
          ''
      );
      setEditCoupon(selectedQueueItem.offer_coupon_code || '');
    }
  }, [selectedQueueItem]);

  const handleSaveQueueItem = async (sendAsNext: boolean = false) => {
    if (!token || !selectedQueueItem) return;
    setIsSavingQueueItem(true);
    try {
      const updated = await updateQueueItem(token, selectedQueueItem.id, {
        custom_title_override: editTitle,
        custom_copy_override: editCopy,
        custom_image_url_override: editImageUrl,
        send_as_next: sendAsNext,
      });
      if (sendAsNext) {
        success(
          `Oferta "${updated.offer_title || 'Oferta'}" reordenada para o topo da esteira pendente com sucesso!`,
          'Disparar como Próximo'
        );
      } else {
        success(
          'Edições da mensagem e imagem salvas com sucesso!',
          'Item Atualizado'
        );
      }
      setSelectedQueueItem(null);
      await loadData();
      window.dispatchEvent(new CustomEvent('operational-health-refresh'));
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Falha ao salvar edições do item.';
      showError(msg, 'Erro ao Salvar');
    } finally {
      setIsSavingQueueItem(false);
    }
  };

  const handleSendAsNextQuick = async (item: QueueItem) => {
    if (!token) return;
    setIsSendingAsNextId(item.id);
    try {
      const updated = await updateQueueItem(token, item.id, {
        send_as_next: true,
      });
      success(
        `Oferta "${updated.offer_title || 'Oferta'}" movida para o topo da fila! Será a próxima a ser disparada.`,
        'Disparar como Próximo'
      );
      await loadData();
      window.dispatchEvent(new CustomEvent('operational-health-refresh'));
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Falha ao mover item para o topo da fila.';
      showError(msg, 'Erro na Fila');
    } finally {
      setIsSendingAsNextId(null);
    }
  };

  // Estados da Tabela Paginada e Filtros
  const [activeTab, setActiveTab] = useState<
    'radar' | 'pending' | 'completed' | 'failed'
  >('radar');
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(
    new Set()
  );
  const [isTechnicalLogExpanded, setIsTechnicalLogExpanded] = useState(false);

  const handleTogglePauseQueue = async () => {
    if (!token) return;
    setIsTogglingPause(true);
    try {
      if (telemetry?.is_paused) {
        await resumeQueue(token);
        success(
          'Esteira de disparos retomada com sucesso!',
          'Esteira Operacional'
        );
      } else {
        await pauseQueue(token);
        success(
          'Esteira de disparos pausada. Os envios automáticos estão suspensos.',
          'Esteira Pausada'
        );
      }
      // Recarrega todos os horários recalculados e telemetria em tempo real
      await loadData();
      window.dispatchEvent(new CustomEvent('operational-health-refresh'));
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Falha ao alterar estado da esteira.';
      showError(msg, 'Erro Operacional');
    } finally {
      setIsTogglingPause(false);
    }
  };

  const handleBatchAction = async (action: 'cancel' | 'retry' | 'requeue') => {
    if (!token || selectedItemIds.size === 0) return;
    setIsExecutingBatch(true);
    try {
      const res = await batchQueueAction(
        token,
        action,
        Array.from(selectedItemIds)
      );
      success(res.message, 'Ação em Lote');
      setSelectedItemIds(new Set());
      await loadData();
      window.dispatchEvent(new CustomEvent('operational-health-refresh'));
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Falha ao executar ação em lote.';
      showError(msg, 'Erro em Lote');
    } finally {
      setIsExecutingBatch(false);
    }
  };

  // Reordenação de itens pendentes via drag and drop
  const handleReorderQueue = async (newOrder: QueueItem[]) => {
    if (!token) return;
    const orderedIds = newOrder.map((it) => it.id);
    // Atualização otimista local
    setItems((prev) => {
      const nonPending = prev.filter((it) => it.status !== 'pending');
      return [...newOrder, ...nonPending];
    });
    try {
      const res = await reorderQueueItems(token, orderedIds);
      success(res.message, 'Fila Reordenada');
      await loadData();
      window.dispatchEvent(new CustomEvent('operational-health-refresh'));
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Falha ao salvar nova ordem.';
      showError(msg, 'Erro na Reordenação');
      await loadData();
    }
  };

  // Diagnóstico amigável para falhas de envio
  const formatFriendlyError = (rawError?: string | null) => {
    if (!rawError) return null;
    const lower = rawError.toLowerCase();
    if (
      lower.includes('buffer has corrupt header') ||
      lower.includes('xml parse error') ||
      lower.includes('glib')
    ) {
      return {
        title: 'Falha no processamento da imagem pela Evolution API',
        summary:
          'A imagem do produto estava em formato incompatível ou bloqueada pelo CDN da loja.',
        recommendation:
          'O sistema fará o envio em modo texto automaticamente no próximo disparo, ou você pode editar o link da imagem na oferta.',
      };
    }
    if (
      lower.includes('connection refused') ||
      lower.includes('offline') ||
      lower.includes('timeout')
    ) {
      return {
        title: 'Instabilidade de conexão com o WhatsApp',
        summary:
          'A Evolution API não conseguiu se comunicar com os servidores do WhatsApp neste momento.',
        recommendation:
          'Aguarde alguns instantes e tente redisparar, ou verifique a conexão na aba WhatsApp.',
      };
    }
    if (
      lower.includes('is not registered') ||
      lower.includes('jid') ||
      lower.includes('group')
    ) {
      return {
        title: 'Grupo de WhatsApp inacessível',
        summary:
          'O número pareado pode ter sido removido do grupo de destino ou o ID é inválido.',
        recommendation:
          'Verifique se o seu chip continua como participante/administrador do grupo.',
      };
    }
    return {
      title: 'Erro operacional no disparo',
      summary:
        rawError.length > 120 ? `${rawError.slice(0, 120)}...` : rawError,
      recommendation: 'Revise os dados da promoção e tente o redisparo.',
    };
  };

  // Itens filtrados para o Radar e Listas Rápidas
  const pendingQueue = items
    .filter((it) => it.status === 'pending' || it.status === 'processing')
    .sort(
      (a, b) =>
        new Date(a.scheduled_for).getTime() -
        new Date(b.scheduled_for).getTime()
    );
  const completedQueue = items
    .filter((it) => it.status === 'completed')
    .sort(
      (a, b) =>
        new Date(b.sent_at || b.updated_at).getTime() -
        new Date(a.sent_at || a.updated_at).getTime()
    );
  const failedQueue = items
    .filter((it) => it.status === 'failed')
    .sort(
      (a, b) =>
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    );

  // Dados para a Tabela Geral
  const filteredTableData = items.filter((it) => {
    if (activeTab === 'pending')
      return it.status === 'pending' || it.status === 'processing';
    if (activeTab === 'completed') return it.status === 'completed';
    if (activeTab === 'failed') return it.status === 'failed';
    return true;
  });

  const toggleSelectItem = (id: string) => {
    const next = new Set(selectedItemIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedItemIds(next);
  };

  const toggleSelectAll = () => {
    if (selectedItemIds.size === filteredTableData.length) {
      setSelectedItemIds(new Set());
    } else {
      setSelectedItemIds(new Set(filteredTableData.map((it) => it.id)));
    }
  };

  const handleConfirmRemove = async () => {
    if (!token || !itemToRemove) return;
    setIsRemoving(true);
    try {
      await cancelQueueItem(token, itemToRemove.id);
      success(
        `Oferta "${itemToRemove.offer_title || 'Oferta'}" removida da fila e devolvida para Aguardando.`,
        'Item Removido'
      );
      setItemToRemove(null);
      await loadData();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Erro ao remover item da esteira.';
      showError(message, 'Falha ao Cancelar');
    } finally {
      setIsRemoving(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!token) return;
    if (settingsForm.dispatch_start_hour >= settingsForm.dispatch_end_hour) {
      showError(
        'O horário de início deve ser menor que o horário de término da janela.',
        'Horário Inválido'
      );
      return;
    }
    if (settingsForm.min_delay_minutes > settingsForm.max_delay_minutes) {
      showError(
        'O intervalo mínimo não pode ser maior que o intervalo máximo.',
        'Intervalo Inválido'
      );
      return;
    }

    setIsSavingSettings(true);
    try {
      const updated = await updateQueueSettings(token, settingsForm);
      setSettingsForm(updated);
      success(
        'Configurações de janela e cadência salvas com sucesso!',
        'Fila Atualizada'
      );
      setIsSettingsDrawerOpen(false);
      // Recarrega telemetria e dados
      const cad = await fetchCadenceTelemetry(token);
      setTelemetry(cad);
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Falha ao salvar configurações.';
      showError(msg, 'Erro ao Salvar');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleSavePacingProfile = async (
    targetQId: string,
    payload: UpdateDispatchQueuePayload
  ) => {
    if (!token) return;
    try {
      if (targetQId === 'all') {
        for (const q of dispatchQueues) {
          await updateDispatchQueue(token, q.id, payload);
        }
      } else {
        await updateDispatchQueue(token, targetQId, payload);
      }
      success(
        'Configurações de Pacing e Silêncio Noturno salvas com sucesso!',
        'Pacing Atualizado'
      );
      await loadData();
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Falha ao salvar configurações de pacing.';
      showError(msg, 'Erro ao Salvar');
      throw err;
    }
  };

  const selectedQueue = dispatchQueues.find((q) => q.id === selectedQueueId);

  const handleSelectQueue = (queueId: string) => {
    setSelectedQueueId(queueId);
    selectedQueueIdRef.current = queueId;
    loadData(queueId);
  };

  const handleToggleSelectedQueuePause = async () => {
    if (!token || !selectedQueue) return;
    setIsTogglingQueuePause(true);
    try {
      if (selectedQueue.is_paused) {
        await resumeDispatchQueue(token, selectedQueue.id);
        success(
          `Fila "${selectedQueue.name}" retomada com sucesso!`,
          'Fila Ativa'
        );
      } else {
        await pauseDispatchQueue(token, selectedQueue.id);
        success(
          `Fila "${selectedQueue.name}" pausada com sucesso!`,
          'Fila Pausada'
        );
      }
      await loadData(selectedQueueId);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Falha ao alterar estado da fila.';
      showError(message, 'Erro na Fila');
    } finally {
      setIsTogglingQueuePause(false);
    }
  };

  const handleCreateQueue = async () => {
    if (!token) return;
    if (!newQueueForm.name?.trim()) {
      showError('O nome da fila é obrigatório.', 'Campo Obrigatório');
      return;
    }
    setIsSavingNewQueue(true);
    try {
      const autoSlug =
        newQueueForm.slug?.trim() ||
        newQueueForm.name
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');

      const created = await createDispatchQueue(token, {
        name: newQueueForm.name.trim(),
        slug: autoSlug,
        strategy_type: newQueueForm.strategy_type || 'daily_deals',
        priority: Number(newQueueForm.priority) || 1,
        target_group_ids: newQueueForm.target_group_ids || [],
      });

      success(`Fila "${created.name}" criada com sucesso!`, 'Fila Criada');
      setIsNewQueueDrawerOpen(false);
      setNewQueueForm({
        name: '',
        slug: '',
        strategy_type: 'daily_deals',
        priority: 1,
        target_group_ids: [],
      });
      setSelectedQueueId(created.id);
      selectedQueueIdRef.current = created.id;
      await loadData(created.id);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Falha ao criar fila de disparo.';
      showError(message, 'Erro ao Criar Fila');
    } finally {
      setIsSavingNewQueue(false);
    }
  };

  // Formatação segura de data com conversão correta de UTC para o horário local do navegador
  const formatDateTime = (isoDate: string) => {
    try {
      const normalized =
        isoDate.includes('Z') || /[+-]\d{2}:\d{2}$/.test(isoDate)
          ? isoDate
          : `${isoDate}Z`;
      const d = new Date(normalized);
      return d.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: '2-digit',
      });
    } catch {
      return isoDate;
    }
  };

  // Determina variação do Badge de status da esteira
  const getHeaderBadge = () => {
    if (isLoading && !whatsappStatus) {
      return (
        <Badge variant="neutral" withDot>
          Verificando Conexão...
        </Badge>
      );
    }
    if (!whatsappStatus?.connected) {
      return (
        <Badge variant="neutral" withDot>
          Aguardando Conexão WhatsApp
        </Badge>
      );
    }
    if (telemetry?.is_paused) {
      return (
        <Badge variant="neutral" withDot>
          Esteira Pausada Manualmente
        </Badge>
      );
    }
    if (!telemetry?.business_hours_active) {
      return (
        <Badge variant="neutral" withDot>
          Aguardando Janela Comercial
        </Badge>
      );
    }
    if (telemetry?.status_level === 'saturated') {
      return (
        <Badge variant="danger" withDot>
          Limite Diário Atingido
        </Badge>
      );
    }
    if (pendingQueue.length === 0) {
      return (
        <Badge variant="neutral" withDot>
          Fila em Repouso
        </Badge>
      );
    }
    return (
      <Badge variant="online" withDot>
        Esteira em Operação
      </Badge>
    );
  };

  // Pré-requisito de despacho ativo
  const hasActiveGroups = groups.some((g) => g.status === 'active');
  const isWhatsAppConnected = Boolean(whatsappStatus?.connected);
  const canDispatch = isWhatsAppConnected && hasActiveGroups;
  const dispatchBlockedReason = !isWhatsAppConnected
    ? 'Conecte o WhatsApp para permitir disparos'
    : !hasActiveGroups
      ? 'Cadastre ao menos 1 grupo ativo para disparar'
      : undefined;

  return (
    <div className="space-y-6">
      {/* 1. Cabeçalho de Tela */}
      <PageHeader
        title="Fila & Disparos Ativos"
        subtitle="Centro operacional de envios para grupos do WhatsApp com proteção anti-ban e gestão de quarentena."
        badge={getHeaderBadge()}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {/* Botão de Pausar / Retomar Fila Global */}
            <Tooltip
              content={
                !canDispatch
                  ? dispatchBlockedReason
                  : telemetry?.is_paused
                    ? 'Retomar o processamento automático da fila de disparos'
                    : 'Pausar temporariamente o envio automático de ofertas'
              }
              position="bottom"
            >
              <Button
                variant={telemetry?.is_paused ? 'primary' : 'secondary'}
                size="sm"
                isLoading={isTogglingPause}
                disabled={!canDispatch}
                onClick={handleTogglePauseQueue}
              >
                {telemetry?.is_paused ? 'Retomar Esteira' : 'Pausar Esteira'}
              </Button>
            </Tooltip>

            {pendingQueue.length > 0 && !telemetry?.is_paused && (
              <Tooltip
                content={
                  !canDispatch
                    ? dispatchBlockedReason
                    : isDispatchingAny
                      ? 'Aguarde o processamento do envio atual...'
                      : 'Enviar imediatamente a próxima oferta agendada da fila'
                }
                position="bottom"
              >
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={!canDispatch || isDispatchingAny}
                  onClick={() =>
                    handleOpenDispatchConfirmation(pendingQueue[0])
                  }
                  isLoading={dispatchingId === pendingQueue[0].id}
                  leftIcon={<Zap className="h-3.5 w-3.5 text-amber-500" />}
                >
                  Disparar Próximo
                </Button>
              </Tooltip>
            )}

            <Button
              variant="secondary"
              size="sm"
              onClick={() => setIsPacingDrawerOpen(true)}
              leftIcon={<Clock className="h-3.5 w-3.5 text-brand-500" />}
            >
              Configurar Ritmo
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void loadData()}
              isLoading={isLoading}
              leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
            >
              Atualizar
            </Button>
          </div>
        }
      />

      {/* Alertas de Pré-requisitos Operacionais */}
      {!whatsappStatus?.connected && (
        <div className="rounded-2xl border border-amber-500/20 bg-amber-500/10 p-4 text-xs text-amber-800 dark:text-amber-300">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 flex-shrink-0 text-amber-500" />
              <div>
                <span className="font-bold">WhatsApp Desconectado:</span>{' '}
                <span>
                  A esteira de disparos não pode enviar mensagens enquanto seu
                  chip não estiver pareado.
                </span>
              </div>
            </div>
            {onNavigate ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onNavigate('whatsapp')}
              >
                Conectar WhatsApp
              </Button>
            ) : (
              <a
                href="/whatsapp"
                className="shadow-xs inline-flex items-center justify-center rounded-xl bg-amber-500 px-3 py-1.5 font-semibold text-white hover:bg-amber-600"
              >
                Conectar WhatsApp
              </a>
            )}
          </div>
        </div>
      )}

      {whatsappStatus?.connected &&
        groups.filter((g) => g.status === 'active').length === 0 && (
          <div className="rounded-2xl border border-blue-500/20 bg-blue-500/10 p-4 text-xs text-blue-800 dark:text-blue-300">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 flex-shrink-0 text-blue-500" />
                <div>
                  <span className="font-bold">Nenhum Grupo Ativo:</span>{' '}
                  <span>
                    Você possui o chip conectado, mas nenhum grupo com status
                    Ativo cadastrado para receber promoções.
                  </span>
                </div>
              </div>
              {onNavigate ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onNavigate('grupos')}
                >
                  Cadastrar Grupos
                </Button>
              ) : (
                <a
                  href="/grupos"
                  className="shadow-xs inline-flex items-center justify-center rounded-xl bg-blue-500 px-3 py-1.5 font-semibold text-white hover:bg-blue-600"
                >
                  Cadastrar Grupos
                </a>
              )}
            </div>
          </div>
        )}

      {/* 2. Seletor de Filas Multi-Queue */}
      <div className="flex flex-col gap-3 rounded-2xl border border-surface-border bg-surface-panel p-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
          <div className="flex items-center gap-1.5 px-2 text-xs font-semibold text-content-muted">
            <Layers className="h-4 w-4 text-brand-500" />
            <span>Filas:</span>
          </div>

          <button
            type="button"
            onClick={() => handleSelectQueue('all')}
            className={`cursor-pointer rounded-xl px-3 py-1.5 text-xs font-medium transition-all ${
              selectedQueueId === 'all'
                ? 'shadow-xs bg-brand-500 font-semibold text-white'
                : 'text-content-muted hover:bg-surface-subtle hover:text-content-main'
            }`}
          >
            Todas as Filas
            <span
              className={`ml-1.5 inline-flex items-center rounded-full px-1.5 py-0.5 text-micro font-bold ${
                selectedQueueId === 'all'
                  ? 'bg-white/20 text-white'
                  : 'bg-surface-subtle text-content-muted'
              }`}
            >
              {dispatchQueues.reduce(
                (acc, q) => acc + (q.pending_count || 0),
                0
              )}
            </span>
          </button>

          {dispatchQueues.map((q) => {
            const isSelected = selectedQueueId === q.id;
            return (
              <button
                key={q.id}
                type="button"
                onClick={() => handleSelectQueue(q.id)}
                className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-xl px-3 py-1.5 text-xs font-medium transition-all ${
                  isSelected
                    ? 'shadow-xs bg-brand-500 font-semibold text-white'
                    : 'text-content-muted hover:bg-surface-subtle hover:text-content-main'
                }`}
              >
                <span>{q.name}</span>
                {q.is_paused && (
                  <span
                    className={`inline-block h-2 w-2 rounded-full ${
                      isSelected ? 'bg-amber-300' : 'bg-amber-500'
                    }`}
                    title="Fila Pausada"
                  />
                )}
                <span
                  className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-micro font-bold ${
                    isSelected
                      ? 'bg-white/20 text-white'
                      : 'bg-surface-subtle text-content-muted'
                  }`}
                >
                  {q.pending_count ?? 0}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          {selectedQueue && (
            <Tooltip
              content={
                selectedQueue.is_paused
                  ? `Retomar envios da fila "${selectedQueue.name}"`
                  : `Pausar envios da fila "${selectedQueue.name}"`
              }
              position="bottom"
            >
              <Button
                variant={selectedQueue.is_paused ? 'primary' : 'secondary'}
                size="sm"
                isLoading={isTogglingQueuePause}
                onClick={handleToggleSelectedQueuePause}
                leftIcon={
                  selectedQueue.is_paused ? (
                    <Play className="h-3.5 w-3.5" />
                  ) : (
                    <Pause className="h-3.5 w-3.5" />
                  )
                }
              >
                {selectedQueue.is_paused ? 'Retomar Fila' : 'Pausar Fila'}
              </Button>
            </Tooltip>
          )}

          <Button
            variant="secondary"
            size="sm"
            onClick={() => setIsNewQueueDrawerOpen(true)}
            leftIcon={<Plus className="h-3.5 w-3.5" />}
          >
            Nova Fila
          </Button>
        </div>
      </div>

      {/* 2. Grid de Telemetria Operacional da Fila */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
        <MetricWidget
          label="Disparos Enviados"
          value={telemetry ? `${telemetry.dispatches_today}` : '--'}
          description="Mensagens entregues hoje com sucesso"
          icon={Send}
        />

        <MetricWidget
          label="Capacidade Diária"
          value={
            telemetry
              ? `${telemetry.dispatches_today} / ${telemetry.safe_capacity_daily}`
              : '--'
          }
          description={
            telemetry
              ? `${telemetry.saturation_percentage}% do teto seguro utilizado`
              : 'Capacidade segura do chip'
          }
          icon={ShieldCheck}
          badge={
            <Badge
              variant={
                telemetry?.status_level === 'saturated'
                  ? 'danger'
                  : telemetry?.status_level === 'warning'
                    ? 'warning'
                    : 'online'
              }
            >
              {telemetry?.saturation_percentage ?? 0}%
            </Badge>
          }
        />

        <MetricWidget
          label="Quarentena de Falhas"
          value={telemetry ? `${telemetry.failed_count}` : '--'}
          description={
            telemetry && telemetry.failed_count > 0
              ? 'Ofertas aguardando intervenção'
              : 'Nenhuma falha operacional'
          }
          icon={AlertCircle}
          badge={
            <Badge
              variant={
                telemetry && telemetry.failed_count > 0 ? 'danger' : 'neutral'
              }
            >
              {telemetry?.failed_count ?? 0}
            </Badge>
          }
        />

        <MetricWidget
          label="Janela Comercial"
          value={
            telemetry?.quiet_hours_active
              ? `Silêncio (${telemetry.quiet_hours_window || '22:30 - 07:30'})`
              : telemetry?.business_hours_window || '08:00 - 22:00'
          }
          description={
            telemetry?.is_paused
              ? 'Esteira pausada manualmente'
              : telemetry?.quiet_hours_active
                ? `Silêncio Noturno ativo. Envios suspensos até ${telemetry.next_window_start ? formatDateTime(telemetry.next_window_start) : '07:30'}`
                : telemetry?.current_pacing_phase === 'prime'
                  ? `Horário Nobre: ${telemetry.jitter_range}`
                  : telemetry?.business_hours_active
                    ? `Intervalo entre disparos: ${telemetry.jitter_range}`
                    : telemetry?.next_window_start
                      ? `Reabre: ${formatDateTime(telemetry.next_window_start)}`
                      : 'Fora do horário permitido'
          }
          icon={Calendar}
          badge={
            <Badge
              variant={
                telemetry?.is_paused
                  ? 'neutral'
                  : telemetry?.quiet_hours_active
                    ? 'warning'
                    : telemetry?.current_pacing_phase === 'prime'
                      ? 'online'
                      : telemetry?.business_hours_active
                        ? 'online'
                        : 'neutral'
              }
            >
              {telemetry?.is_paused
                ? 'Pausada'
                : telemetry?.quiet_hours_active
                  ? 'Silêncio Ativo'
                  : telemetry?.current_pacing_phase === 'prime'
                    ? 'Horário Nobre'
                    : telemetry?.business_hours_active
                      ? 'Aberta'
                      : 'Fechada'}
            </Badge>
          }
        />
      </div>

      {/* Banner de Quarentena / Falhas Operacionais */}
      {telemetry && telemetry.failed_count > 0 && (
        <div className="rounded-2xl border border-red-500/20 bg-red-500/10 p-4 text-xs text-red-800 dark:text-red-300">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 text-red-500" />
              <div>
                <span className="font-bold">
                  {telemetry.failed_count} Oferta(s) em Quarentena:
                </span>{' '}
                <span>
                  Ocorreram falhas no disparo. A esteira principal continua
                  operando normalmente, mas esses itens necessitam de revisão.
                </span>
              </div>
            </div>
            <Button
              variant="danger"
              size="sm"
              onClick={() => setActiveTab('failed')}
              leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
            >
              Ver e Resolver Falhas
            </Button>
          </div>
        </div>
      )}

      {/* 3. Navegação por Abas (Radar vs Tabela Completa) */}
      <div className="space-y-4">
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <div className="flex items-center gap-1 overflow-x-auto rounded-xl border border-surface-border bg-surface-subtle p-1">
            <button
              type="button"
              onClick={() => setActiveTab('radar')}
              className={`cursor-pointer rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                activeTab === 'radar'
                  ? 'shadow-xs bg-surface-panel font-semibold text-content-main'
                  : 'text-content-muted hover:text-content-main'
              }`}
            >
              Radar Operacional
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('pending')}
              className={`cursor-pointer rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                activeTab === 'pending'
                  ? 'shadow-xs bg-surface-panel font-semibold text-content-main'
                  : 'text-content-muted hover:text-content-main'
              }`}
            >
              Agendados ({pendingQueue.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('completed')}
              className={`cursor-pointer rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                activeTab === 'completed'
                  ? 'shadow-xs bg-surface-panel font-semibold text-content-main'
                  : 'text-content-muted hover:text-content-main'
              }`}
            >
              Enviados ({completedQueue.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('failed')}
              className={`cursor-pointer rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                activeTab === 'failed'
                  ? 'shadow-xs bg-surface-panel font-semibold text-red-600 dark:text-red-400'
                  : 'text-content-muted hover:text-content-main'
              }`}
            >
              Quarentena ({failedQueue.length})
            </button>
          </div>

          {/* Barra de Ações em Lote quando há seleção na tabela */}
          {activeTab !== 'radar' && selectedItemIds.size > 0 && (
            <div className="shadow-xs flex items-center gap-2 rounded-xl border border-surface-border bg-surface-panel px-3 py-1.5">
              <span className="text-xs font-semibold text-content-main">
                {selectedItemIds.size} selecionado(s)
              </span>
              <Button
                variant="ghost"
                size="sm"
                isLoading={isExecutingBatch}
                onClick={() => handleBatchAction('cancel')}
                leftIcon={<Trash2 className="h-3.5 w-3.5 text-red-500" />}
              >
                Remover da Fila
              </Button>
              {activeTab === 'failed' && (
                <>
                  <Button
                    variant="primary"
                    size="sm"
                    isLoading={isExecutingBatch}
                    onClick={() => handleBatchAction('requeue')}
                    leftIcon={<Clock className="h-3.5 w-3.5" />}
                    title="Devolve as ofertas selecionadas para a fila respeitando a cadência e jitter anti-ban"
                  >
                    Re-enfileirar na Esteira
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    isLoading={isExecutingBatch}
                    onClick={() => handleBatchAction('retry')}
                    leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
                    title="Dispara imediatamente sem aguardar intervalo"
                  >
                    Redisparar Imediato
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        {loadError && items.length === 0 ? (
          <Card>
            <Card.Body className="mx-auto max-w-md space-y-4 px-4 py-12 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border border-red-500/20 bg-red-500/10 text-red-600 dark:text-red-400">
                <AlertCircle className="h-6 w-6" />
              </div>
              <div className="space-y-1">
                <h4 className="text-sm font-semibold text-content-main">
                  Não foi possível recuperar os dados da API
                </h4>
                <p className="text-xs leading-relaxed text-content-muted">
                  Ocorreu uma falha ao sincronizar os itens da esteira de
                  disparo. Por favor, tente novamente.
                </p>
              </div>
              <Button
                variant="primary"
                size="sm"
                leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
                onClick={() => void loadData()}
              >
                Tentar Novamente
              </Button>
            </Card.Body>
          </Card>
        ) : activeTab === 'radar' ? (
          <div className="space-y-6">
            {/* Bloco 1: Próximos a Disparar */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-content-main">
                  Próximos a Disparar
                </h3>
                {/* <span className="text-micro text-content-muted">
                  Ordem estocástica com Jitter RNG
                </span> */}
              </div>

              {pendingQueue.length === 0 ? (
                <Card>
                  <Card.Body className="py-8 text-center">
                    <Clock className="text-content-muted/50 mx-auto h-6 w-6" />
                    <p className="mt-2 text-xs font-medium text-content-main">
                      Nenhum envio agendado na fila
                    </p>
                    <p className="mt-0.5 text-2xs text-content-muted">
                      Cadastre novas ofertas ou envie as salvas para a fila.
                    </p>
                  </Card.Body>
                </Card>
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {pendingQueue.slice(0, 3).map((item, idx) => (
                    <Card
                      key={item.id}
                      className="cursor-pointer transition-all hover:border-brand-500"
                      onClick={() => setSelectedQueueItem(item)}
                    >
                      <Card.Body className="space-y-3 p-4">
                        <div className="flex items-start gap-3">
                          <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg bg-brand-500/10 text-xs font-bold text-brand-600 dark:text-brand-400">
                            #{idx + 1}
                          </span>
                          {item.offer_image_url ? (
                            <img
                              src={item.offer_image_url}
                              alt={item.offer_title || 'Imagem'}
                              className="h-10 w-10 flex-shrink-0 rounded-lg border border-surface-border object-cover"
                            />
                          ) : (
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-surface-border bg-surface-subtle text-content-muted">
                              <Clock className="h-4 w-4" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <h4 className="truncate text-xs font-semibold text-content-main">
                              {item.offer_title || 'Oferta sem título'}
                            </h4>
                            <div className="mt-1 flex items-center gap-2 text-micro text-content-muted">
                              <span className="font-semibold text-content-main">
                                {item.queue_name || 'Principal'}
                              </span>
                              <span>•</span>
                              <span>{item.group_ids.length} grupo(s)</span>
                              <span>•</span>
                              <span>{formatDateTime(item.scheduled_for)}</span>
                            </div>
                          </div>
                        </div>

                        <div className="border-surface-border/50 flex items-center justify-between border-t pt-2.5">
                          {item.status === 'processing' ? (
                            <Badge variant="warning" withDot pulse>
                              Enviando...
                            </Badge>
                          ) : (
                            <Badge variant="info" withDot>
                              Agendado
                            </Badge>
                          )}
                          <div
                            className="flex items-center gap-1.5"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {idx > 0 && item.status === 'pending' && (
                              <Tooltip
                                content="Disparar como Próximo: fura a fila e move este item para o topo da esteira"
                                position="left"
                              >
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  disabled={
                                    isDispatchingAny ||
                                    isSendingAsNextId === item.id
                                  }
                                  isLoading={isSendingAsNextId === item.id}
                                  onClick={() => handleSendAsNextQuick(item)}
                                  title="Disparar como Próximo"
                                >
                                  <ArrowUpToLine className="h-3 w-3 text-brand-500" />
                                </Button>
                              </Tooltip>
                            )}
                            <Tooltip
                              content={
                                item.status === 'processing'
                                  ? 'Disparo em andamento...'
                                  : !canDispatch
                                    ? dispatchBlockedReason
                                    : isDispatchingAny
                                      ? 'Aguarde o envio em processamento...'
                                      : 'Disparar esta oferta agora sem aguardar o horário'
                              }
                              position="left"
                            >
                              <Button
                                variant="secondary"
                                size="sm"
                                disabled={
                                  !canDispatch ||
                                  item.status === 'processing' ||
                                  isDispatchingAny
                                }
                                isLoading={
                                  dispatchingId === item.id ||
                                  item.status === 'processing'
                                }
                                onClick={() =>
                                  handleOpenDispatchConfirmation(item)
                                }
                                leftIcon={
                                  <Zap className="h-3 w-3 text-amber-500" />
                                }
                              >
                                Enviar Já
                              </Button>
                            </Tooltip>
                            <Tooltip content="Aplicar Cupom com 1 Clique">
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setCouponTargetQueueItem(item)}
                                leftIcon={
                                  <Ticket className="h-3 w-3 text-brand-500" />
                                }
                              >
                                Cupom
                              </Button>
                            </Tooltip>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedQueueItem(item)}
                              title="Ver Detalhes e Editar"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Bloco 2: Últimos Enviados com Sucesso */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-content-main">
                  Últimos Enviados
                </h3>
                {/* <span className="text-micro text-content-muted">
                  Disparos entregues com sucesso
                </span> */}
              </div>

              {completedQueue.length === 0 ? (
                <Card>
                  <Card.Body className="py-8 text-center">
                    <CheckCircle2 className="text-content-muted/50 mx-auto h-6 w-6" />
                    <p className="mt-2 text-xs font-medium text-content-main">
                      Nenhum disparo enviado hoje ainda
                    </p>
                  </Card.Body>
                </Card>
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {completedQueue.slice(0, 3).map((item) => (
                    <Card
                      key={item.id}
                      className="cursor-pointer transition-all hover:border-surface-border"
                      onClick={() => setSelectedQueueItem(item)}
                    >
                      <Card.Body className="space-y-3 p-4">
                        <div className="flex items-start gap-3">
                          {item.offer_image_url ? (
                            <img
                              src={item.offer_image_url}
                              alt={item.offer_title || 'Imagem'}
                              className="h-10 w-10 flex-shrink-0 rounded-lg border border-surface-border object-cover"
                            />
                          ) : (
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-surface-border bg-surface-subtle text-content-muted">
                              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <h4 className="truncate text-xs font-semibold text-content-main">
                              {item.offer_title || 'Oferta sem título'}
                            </h4>
                            <div className="mt-1 flex items-center gap-2 text-micro text-content-muted">
                              <span className="font-semibold text-content-main">
                                {item.queue_name || 'Principal'}
                              </span>
                              <span>•</span>
                              <span>{item.group_ids.length} grupo(s)</span>
                              <span>•</span>
                              <span>
                                {item.sent_at
                                  ? formatDateTime(item.sent_at)
                                  : formatDateTime(item.updated_at)}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="border-surface-border/50 flex items-center justify-between border-t pt-2.5">
                          <Badge variant="online" withDot>
                            Entregue
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedQueueItem(item);
                            }}
                          >
                            Ver Detalhes
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          /* CONTEÚDO DA TABELA COMPLETA COM PAGINAÇÃO E SELEÇÃO MÚLTIPLA */
          <div className="space-y-3">
            {activeTab === 'pending' && pendingQueue.length > 1 && (
              <div className="flex items-center gap-2 text-micro text-content-muted">
                <GripVertical className="h-3.5 w-3.5 text-brand-500" />
                <span>
                  Arraste uma linha para reorganizar a ordem de disparo da fila.
                </span>
              </div>
            )}
            <DataTable<QueueItem>
              data={filteredTableData}
              keyExtractor={(it) => it.id}
              pageSize={10}
              onRowClick={(it) => setSelectedQueueItem(it)}
              onReorder={
                activeTab === 'pending' ? handleReorderQueue : undefined
              }
              emptyMessage={
                <div className="py-12 text-center">
                  <Clock className="text-content-muted/40 mx-auto h-8 w-8" />
                  <p className="mt-3 text-sm font-semibold text-content-main">
                    Nenhum item encontrado nesta visualização
                  </p>
                  <p className="mt-1 text-xs text-content-muted">
                    Alterne o filtro ou adicione novas ofertas à fila.
                  </p>
                </div>
              }
              columns={[
                ...(activeTab === 'pending'
                  ? [
                      {
                        key: 'reorder_handle',
                        header: '',
                        render: () => (
                          <div className="text-content-muted/40 flex items-center justify-center hover:text-content-muted">
                            <GripVertical className="h-4 w-4" />
                          </div>
                        ),
                        className: 'w-8 text-center cursor-grab',
                      },
                    ]
                  : []),
                {
                  key: 'select',
                  header: (
                    <input
                      type="checkbox"
                      checked={
                        filteredTableData.length > 0 &&
                        selectedItemIds.size === filteredTableData.length
                      }
                      onChange={toggleSelectAll}
                      className="rounded border-surface-border"
                    />
                  ),
                  render: (it) => (
                    <div onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedItemIds.has(it.id)}
                        onChange={() => toggleSelectItem(it.id)}
                        className="rounded border-surface-border"
                      />
                    </div>
                  ),
                  className: 'w-10 text-center',
                },
                {
                  key: 'offer',
                  header: 'Oferta',
                  render: (it) => (
                    <div className="flex items-center gap-3">
                      {it.offer_image_url ? (
                        <img
                          src={it.offer_image_url}
                          alt={it.offer_title || ''}
                          className="h-9 w-9 flex-shrink-0 rounded-lg border border-surface-border object-cover"
                        />
                      ) : (
                        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-surface-border bg-surface-subtle text-content-muted">
                          <Clock className="h-4 w-4" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="truncate text-xs font-semibold text-content-main">
                          {it.offer_title || 'Oferta sem título'}
                        </div>
                        <div className="text-micro text-content-muted">
                          {it.group_ids.length} grupo(s) de destino
                        </div>
                      </div>
                    </div>
                  ),
                },
                {
                  key: 'queue',
                  header: 'Fila',
                  render: (it) => (
                    <Badge variant="neutral">
                      {it.queue_name || 'Principal'}
                    </Badge>
                  ),
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (it) => {
                    if (it.status === 'completed') {
                      return (
                        <Badge variant="online" withDot>
                          Enviado
                        </Badge>
                      );
                    }
                    if (it.status === 'processing') {
                      return (
                        <Badge variant="warning" withDot pulse>
                          Processando
                        </Badge>
                      );
                    }
                    if (it.status === 'pending') {
                      return (
                        <Badge variant="info" withDot>
                          Agendado
                        </Badge>
                      );
                    }
                    if (it.status === 'failed') {
                      const deliveredCount =
                        it.delivered_group_ids?.length || 0;
                      if (deliveredCount > 0) {
                        return (
                          <div className="flex flex-col gap-1">
                            <Badge variant="warning" withDot pulse>
                              Parcial ({deliveredCount}/{it.group_ids.length})
                            </Badge>
                            <span className="text-micro text-content-muted">
                              Em Quarentena
                            </span>
                          </div>
                        );
                      }
                      return (
                        <Badge variant="danger" withDot>
                          Falhou
                        </Badge>
                      );
                    }
                    return <Badge variant="neutral">{it.status}</Badge>;
                  },
                },
                {
                  key: 'schedule',
                  header: 'Programado / Enviado',
                  render: (it) => (
                    <div className="text-micro text-content-muted">
                      {it.sent_at ? (
                        <span className="text-emerald-600 dark:text-emerald-400">
                          Entregue: {formatDateTime(it.sent_at)}
                        </span>
                      ) : (
                        <span>
                          Agendado: {formatDateTime(it.scheduled_for)}
                        </span>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'actions',
                  header: 'Ações',
                  render: (it) => (
                    <div
                      className="flex items-center gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {it.status === 'processing' && (
                        <Tooltip
                          content="Disparo em andamento..."
                          position="left"
                        >
                          <Button
                            variant="secondary"
                            size="sm"
                            disabled
                            isLoading
                          >
                            <Zap className="h-3 w-3 text-amber-500" />
                          </Button>
                        </Tooltip>
                      )}
                      {it.status === 'pending' && (
                        <>
                          <Tooltip
                            content="Disparar como Próximo: fura a fila e reordena este item para o topo da esteira pendente"
                            position="left"
                          >
                            <Button
                              variant="secondary"
                              size="sm"
                              disabled={
                                isDispatchingAny || isSendingAsNextId === it.id
                              }
                              isLoading={isSendingAsNextId === it.id}
                              onClick={() => handleSendAsNextQuick(it)}
                              title="Disparar como Próximo"
                            >
                              <ArrowUpToLine className="h-3 w-3 text-brand-500" />
                            </Button>
                          </Tooltip>
                          <Tooltip
                            content={
                              !canDispatch
                                ? dispatchBlockedReason
                                : isDispatchingAny
                                  ? 'Aguarde o envio em processamento...'
                                  : 'Disparar esta oferta agora sem aguardar o horário'
                            }
                            position="left"
                          >
                            <Button
                              variant="secondary"
                              size="sm"
                              disabled={!canDispatch || isDispatchingAny}
                              isLoading={dispatchingId === it.id}
                              onClick={() => handleOpenDispatchConfirmation(it)}
                              title="Enviar Já"
                            >
                              <Zap className="h-3 w-3 text-amber-500" />
                            </Button>
                          </Tooltip>
                          <Tooltip
                            content="Aplicar Cupom com 1 Clique"
                            position="left"
                          >
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => setCouponTargetQueueItem(it)}
                              title="Aplicar Cupom"
                            >
                              <Ticket className="h-3 w-3 text-brand-500" />
                            </Button>
                          </Tooltip>
                        </>
                      )}
                      {it.status === 'failed' && (
                        <>
                          <Tooltip
                            content={
                              isDispatchingAny
                                ? 'Aguarde o envio em processamento...'
                                : 'Devolver à fila com cadência e intervalo anti-ban calculados'
                            }
                            position="left"
                          >
                            <Button
                              variant="secondary"
                              size="sm"
                              disabled={isDispatchingAny}
                              isLoading={isRequeuingId === it.id}
                              onClick={() => handleRequeueSingleItem(it)}
                              title="Re-enfileirar na Esteira"
                            >
                              <Clock className="h-3 w-3 text-brand-500" />
                            </Button>
                          </Tooltip>
                          <Tooltip
                            content={
                              !canDispatch
                                ? dispatchBlockedReason
                                : isDispatchingAny
                                  ? 'Aguarde o envio em processamento...'
                                  : 'Disparar agora imediatamente (bypass da fila)'
                            }
                            position="left"
                          >
                            <Button
                              variant="secondary"
                              size="sm"
                              disabled={!canDispatch || isDispatchingAny}
                              isLoading={dispatchingId === it.id}
                              onClick={() => handleOpenDispatchConfirmation(it)}
                              title="Disparar Agora Imediatamente"
                            >
                              <RotateCcw className="h-3 w-3 text-amber-500" />
                            </Button>
                          </Tooltip>
                        </>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedQueueItem(it)}
                        title="Ver Detalhes"
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ),
                  className: 'text-right',
                },
              ]}
            />
          </div>
        )}
      </div>

      {/* PAINEL LATERAL (DRAWER) DE DETALHAMENTO DO ITEM NA FILA */}
      <Drawer
        isOpen={Boolean(selectedQueueItem)}
        onClose={() => setSelectedQueueItem(null)}
        title={
          selectedQueueItem?.status === 'pending'
            ? 'Editar Disparo & WhatsApp Preview'
            : 'Detalhes do Disparo na Fila'
        }
        width="lg"
        footer={
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              {selectedQueueItem &&
                selectedQueueItem.status !== 'completed' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setItemToRemove(selectedQueueItem);
                      setSelectedQueueItem(null);
                    }}
                    leftIcon={<Trash2 className="h-3.5 w-3.5 text-red-500" />}
                  >
                    Remover da Fila
                  </Button>
                )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSelectedQueueItem(null)}
              >
                Fechar
              </Button>
              {selectedQueueItem && selectedQueueItem.status === 'pending' && (
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    isLoading={isSavingQueueItem}
                    onClick={() => handleSaveQueueItem(false)}
                  >
                    Salvar Alterações
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    isLoading={isSavingQueueItem}
                    onClick={() => handleSaveQueueItem(true)}
                    leftIcon={<ArrowUpToLine className="h-3.5 w-3.5" />}
                    title="Salva as alterações e move o item para o topo da esteira pendente"
                  >
                    Disparar como Próximo
                  </Button>
                </>
              )}
              {selectedQueueItem && selectedQueueItem.status === 'failed' && (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={isDispatchingAny}
                  isLoading={isRequeuingId === selectedQueueItem.id}
                  onClick={() => handleRequeueSingleItem(selectedQueueItem)}
                  leftIcon={<Clock className="h-3.5 w-3.5" />}
                  title="Devolve para a esteira com cadência e jitter calculados"
                >
                  Re-enfileirar na Esteira
                </Button>
              )}
              {selectedQueueItem &&
                selectedQueueItem.status !== 'completed' && (
                  <>
                    {selectedQueueItem.status === 'pending' && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          setCouponTargetQueueItem(selectedQueueItem)
                        }
                        leftIcon={
                          <Ticket className="h-3.5 w-3.5 text-brand-500" />
                        }
                      >
                        Aplicar Cupom
                      </Button>
                    )}
                    <Tooltip
                      content={
                        !canDispatch
                          ? dispatchBlockedReason
                          : isDispatchingAny
                            ? 'Aguarde o envio em processamento...'
                            : selectedQueueItem.status === 'failed'
                              ? 'Reenviar disparo imediatamente (bypass da fila)'
                              : 'Disparar esta oferta agora para os grupos'
                      }
                      position="top"
                    >
                      <Button
                        variant={
                          selectedQueueItem.status === 'pending'
                            ? 'secondary'
                            : 'primary'
                        }
                        size="sm"
                        disabled={!canDispatch || isDispatchingAny}
                        isLoading={dispatchingId === selectedQueueItem.id}
                        onClick={() => {
                          handleOpenDispatchConfirmation(selectedQueueItem);
                        }}
                        leftIcon={<Zap className="h-3.5 w-3.5" />}
                      >
                        {selectedQueueItem.status === 'failed'
                          ? 'Tentar Novamente Agora'
                          : 'Enviar Agora'}
                      </Button>
                    </Tooltip>
                  </>
                )}
            </div>
          </div>
        }
      >
        {selectedQueueItem && selectedQueueItem.status === 'pending' && (
          <div className="space-y-5 p-5">
            {/* Banner Informativo */}
            <div className="flex flex-col gap-2 rounded-xl border border-brand-500/20 bg-brand-500/5 p-3 text-xs sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-0.5">
                <span className="font-bold text-content-main">
                  Edição Manual de Última Hora
                </span>
                <p className="text-2xs text-content-muted">
                  Personalize copy, imagem e cupom. O preview do WhatsApp
                  sincroniza em tempo real.
                </p>
              </div>
              <Badge variant="info" withDot>
                Agendado: {formatDateTime(selectedQueueItem.scheduled_for)}
              </Badge>
            </div>

            {/* Inputs de Formulário */}
            <div className="space-y-3.5">
              <Input
                label="Título do Produto / Chamada"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Título da oferta no WhatsApp..."
              />

              <Input
                label="URL da Imagem de Destaque"
                value={editImageUrl}
                onChange={(e) => setEditImageUrl(e.target.value)}
                placeholder="https://exemplo.com/imagem.jpg"
              />

              <Input
                label="Cupom Promocional (opcional)"
                value={editCoupon}
                onChange={(e) => setEditCoupon(e.target.value)}
                placeholder="Ex: PROMO10"
              />
            </div>

            {/* Balão Interativo WhatsApp Preview */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-content-main">
                  Mensagem WhatsApp (Copy & Preview Interativo)
                </label>
                <span className="text-2xs text-content-muted">
                  Edite o texto diretamente no balão
                </span>
              </div>
              <WhatsAppBubblePreview
                isEditable={true}
                imageUrl={editImageUrl || selectedQueueItem.offer_image_url}
                title={editTitle || selectedQueueItem.offer_title || ''}
                text={editCopy}
                onTextChange={(newText) => setEditCopy(newText)}
                originalPrice={selectedQueueItem.offer_original_price}
                promotionalPrice={selectedQueueItem.offer_promotional_price}
                couponCode={editCoupon || selectedQueueItem.offer_coupon_code}
              />
            </div>

            {/* Destino e Horário */}
            <div className="space-y-3 rounded-xl border border-surface-border bg-surface-panel p-4">
              <h4 className="text-xs font-semibold text-content-main">
                Destino e Horário da Fila
              </h4>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <span className="text-content-muted">
                    Horário Programado:
                  </span>
                  <p className="font-semibold text-content-main">
                    {formatDateTime(selectedQueueItem.scheduled_for)}
                  </p>
                </div>
                <div>
                  <span className="text-content-muted">Fila de Disparo:</span>
                  <p className="font-semibold text-content-main">
                    {selectedQueueItem.queue_name || 'Fila Principal'}
                  </p>
                </div>
                <div className="col-span-2">
                  <span className="text-content-muted">Grupos Alvo:</span>
                  <p className="font-semibold text-content-main">
                    {selectedQueueItem.group_ids.length} grupo(s) configurado(s)
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedQueueItem && selectedQueueItem.status !== 'pending' && (
          <div className="space-y-6 p-5">
            {/* Header da Oferta */}
            <div className="flex items-start gap-4">
              {selectedQueueItem.offer_image_url ? (
                <img
                  src={selectedQueueItem.offer_image_url}
                  alt={selectedQueueItem.offer_title || ''}
                  className="h-16 w-16 flex-shrink-0 rounded-xl border border-surface-border object-cover"
                />
              ) : (
                <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-xl border border-surface-border bg-surface-subtle text-content-muted">
                  <Clock className="h-6 w-6" />
                </div>
              )}
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-bold text-content-main">
                    {selectedQueueItem.offer_title || 'Oferta sem título'}
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  {selectedQueueItem.status === 'completed' && (
                    <Badge variant="online" withDot>
                      Enviado com Sucesso
                    </Badge>
                  )}
                  {selectedQueueItem.status === 'failed' && (
                    <Badge variant="danger" withDot>
                      Falhou
                    </Badge>
                  )}
                  {selectedQueueItem.status === 'processing' && (
                    <Badge variant="warning" withDot pulse>
                      Disparando
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Ações e Links de Navegação */}
            {onNavigate && (
              <div className="rounded-xl border border-surface-border bg-surface-subtle p-3 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-content-muted">
                    Precisa alterar título, preço ou cópia da oferta?
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      const offerId = selectedQueueItem.offer_id;
                      setSelectedQueueItem(null);
                      onNavigate('ofertas', offerId);
                    }}
                    rightIcon={<ExternalLink className="h-3 w-3" />}
                  >
                    Editar
                  </Button>
                </div>
              </div>
            )}

            {/* Metadados da Esteira */}
            <div className="space-y-3 rounded-xl border border-surface-border bg-surface-panel p-4">
              <h4 className="text-xs font-semibold text-content-main">
                Dados de Telemetria do Envio
              </h4>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <span className="text-content-muted">
                    Horário Programado:
                  </span>
                  <p className="font-semibold text-content-main">
                    {formatDateTime(selectedQueueItem.scheduled_for)}
                  </p>
                </div>
                <div>
                  <span className="text-content-muted">
                    Horário de Entrega:
                  </span>
                  <p className="font-semibold text-content-main">
                    {selectedQueueItem.sent_at
                      ? formatDateTime(selectedQueueItem.sent_at)
                      : '--'}
                  </p>
                </div>
                <div>
                  <span className="text-content-muted">
                    Tentativas Executadas:
                  </span>
                  <p className="font-semibold text-content-main">
                    {selectedQueueItem.retry_count} /{' '}
                    {selectedQueueItem.max_retries}
                  </p>
                </div>
                <div>
                  <span className="text-content-muted">Grupos Alvo:</span>
                  <p className="font-semibold text-content-main">
                    {selectedQueueItem.group_ids.length} grupo(s)
                    {Boolean(selectedQueueItem.delivered_group_ids?.length) && (
                      <span className="ml-1.5 text-micro font-normal text-emerald-600 dark:text-emerald-400">
                        ({selectedQueueItem.delivered_group_ids?.length} já
                        entregue(s))
                      </span>
                    )}
                  </p>
                </div>
              </div>

              {/* Lista dos grupos vinculados com status individual */}
              <div className="border-t border-surface-border pt-3">
                <span className="mb-2 block text-micro font-semibold uppercase tracking-wider text-content-muted">
                  Destinos e Entregas:
                </span>
                <div className="space-y-1.5">
                  {selectedQueueItem.group_ids.map((gid) => {
                    const groupObj = groups.find(
                      (g) => g.id === gid || g.jid === gid
                    );
                    const name = groupObj ? groupObj.name : gid;
                    const isDelivered =
                      selectedQueueItem.delivered_group_ids?.includes(gid) ||
                      Boolean(
                        groupObj?.jid &&
                        selectedQueueItem.delivered_group_ids?.includes(
                          groupObj.jid
                        )
                      );
                    return (
                      <div
                        key={gid}
                        className="flex items-center justify-between rounded-lg border border-surface-border bg-surface-subtle px-3 py-1.5 text-xs"
                      >
                        <span className="max-w-[200px] truncate font-medium text-content-main">
                          {name}
                        </span>
                        {isDelivered ? (
                          <Badge variant="online" withDot>
                            Entregue
                          </Badge>
                        ) : selectedQueueItem.status === 'completed' ? (
                          <Badge variant="online" withDot>
                            Entregue
                          </Badge>
                        ) : selectedQueueItem.status === 'failed' ? (
                          <Badge variant="danger" withDot>
                            Não Enviado
                          </Badge>
                        ) : (
                          <Badge variant="neutral">Pendente</Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Se Falhou: Exibição Amigável com Detalhamento Técnico Opcional */}
            {selectedQueueItem.last_error &&
              (() => {
                const friendly = formatFriendlyError(
                  selectedQueueItem.last_error
                );
                return (
                  <div className="space-y-3 rounded-xl border border-red-500/20 bg-red-500/5 p-4 text-xs">
                    <div className="flex items-start gap-2.5">
                      <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
                      <div className="min-w-0 flex-1 space-y-1">
                        <h5 className="font-bold text-red-700 dark:text-red-400">
                          {friendly?.title || 'Falha no Disparo'}
                        </h5>
                        <p className="text-micro text-content-main">
                          {friendly?.summary}
                        </p>
                        <p className="text-micro text-content-muted">
                          <strong>Recomendação:</strong>{' '}
                          {friendly?.recommendation}
                        </p>
                      </div>
                    </div>

                    {/* Detalhe Técnico Colapsável */}
                    <div className="border-t border-red-500/10 pt-2">
                      <button
                        type="button"
                        onClick={() =>
                          setIsTechnicalLogExpanded((prev) => !prev)
                        }
                        className="flex w-full cursor-pointer items-center justify-between text-micro font-medium text-red-700 hover:underline dark:text-red-300"
                      >
                        <span>Ver detalhes técnicos do erro</span>
                        {isTechnicalLogExpanded ? (
                          <ChevronUp className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5" />
                        )}
                      </button>

                      {isTechnicalLogExpanded && (
                        <div className="mt-2 space-y-2">
                          <div className="flex items-center justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (selectedQueueItem.last_error) {
                                  navigator.clipboard.writeText(
                                    selectedQueueItem.last_error
                                  );
                                  setIsCopyingError(true);
                                  setTimeout(
                                    () => setIsCopyingError(false),
                                    2000
                                  );
                                }
                              }}
                              leftIcon={
                                isCopyingError ? (
                                  <Check className="h-3 w-3 text-emerald-500" />
                                ) : (
                                  <Copy className="h-3 w-3" />
                                )
                              }
                            >
                              {isCopyingError ? 'Copiado' : 'Copiar Log'}
                            </Button>
                          </div>
                          <pre className="max-h-36 overflow-y-auto rounded-lg border border-surface-border bg-surface-canvas p-2.5 font-mono text-micro leading-relaxed text-red-700 dark:text-red-300">
                            {selectedQueueItem.last_error}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
          </div>
        )}
      </Drawer>

      {/* Modal de Confirmação para Remoção da Fila */}
      <ConfirmationModal
        isOpen={Boolean(itemToRemove)}
        onClose={() => setItemToRemove(null)}
        onConfirm={handleConfirmRemove}
        isLoading={isRemoving}
        variant="danger"
        title="Remover Oferta da Fila"
        description={
          itemToRemove ? (
            <span>
              Tem certeza que deseja remover o disparo da oferta{' '}
              <strong>"{itemToRemove.offer_title || 'Oferta'}"</strong> da fila?
              A promoção retornará para o status <strong>"Aguardando"</strong>{' '}
              na aba de Ofertas.
            </span>
          ) : null
        }
        confirmText="Sim, Remover da Fila"
        cancelText="Voltar"
      />

      {/* Modal de Confirmação para Disparo Imediato (Bypass) */}
      <ConfirmationModal
        isOpen={Boolean(itemToDispatchNow)}
        onClose={() => setItemToDispatchNow(null)}
        onConfirm={handleConfirmDispatchNow}
        isLoading={Boolean(
          dispatchingId &&
          itemToDispatchNow &&
          dispatchingId === itemToDispatchNow.id
        )}
        variant="primary"
        title="Confirmar Disparo Imediato"
        description={
          itemToDispatchNow ? (
            <div className="space-y-2">
              <p>
                Deseja disparar imediatamente a oferta{' '}
                <strong>"{itemToDispatchNow.offer_title || 'Oferta'}"</strong>?
              </p>
              <div className="rounded-lg border border-surface-border bg-surface-subtle p-3 text-xs text-content-muted">
                <p>
                  • <strong>Destino:</strong>{' '}
                  {itemToDispatchNow.group_ids.length} grupo(s) de WhatsApp
                  configurado(s).
                </p>
                <p className="mt-1">
                  • <strong>Atenção anti-ban:</strong> Este envio fura a fila e
                  executa agora na Evolution API sem aguardar o intervalo
                  estocástico da esteira.
                </p>
              </div>
            </div>
          ) : null
        }
        confirmText="Sim, Disparar Agora"
        cancelText="Cancelar"
      />

      {/* Drawer de Configurações da Fila & Janela */}
      <Drawer
        isOpen={isSettingsDrawerOpen}
        onClose={() => setIsSettingsDrawerOpen(false)}
        title="Configurações da Fila & Cadência"
        width="md"
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setIsSettingsDrawerOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="sm"
              isLoading={isSavingSettings}
              onClick={handleSaveSettings}
            >
              Salvar Configurações
            </Button>
          </div>
        }
      >
        <div className="space-y-5 p-5">
          <p className="text-xs leading-relaxed text-content-muted">
            Personalize a janela comercial de funcionamento dos disparos
            automáticos e os limites de intervalo anti-spam para proteger seus
            números de WhatsApp.
          </p>

          <div className="space-y-4 rounded-xl border border-surface-border bg-surface-subtle p-4">
            <h4 className="text-xs font-semibold text-content-main">
              Janela Comercial de Disparo
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Início dos Envios (Hora)"
                type="number"
                min={0}
                max={23}
                value={String(settingsForm.dispatch_start_hour)}
                onChange={(e) =>
                  setSettingsForm({
                    ...settingsForm,
                    dispatch_start_hour: Number(e.target.value),
                  })
                }
                helperText="Ex: 8 para 08:00"
              />
              <Input
                label="Término dos Envios (Hora)"
                type="number"
                min={1}
                max={24}
                value={String(settingsForm.dispatch_end_hour)}
                onChange={(e) =>
                  setSettingsForm({
                    ...settingsForm,
                    dispatch_end_hour: Number(e.target.value),
                  })
                }
                helperText="Ex: 22 para 22:00"
              />
            </div>
          </div>

          <div className="space-y-4 rounded-xl border border-surface-border bg-surface-subtle p-4">
            <h4 className="text-xs font-semibold text-content-main">
              Intervalos Anti-Spam (Cadência de Linha)
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Espera Mínima (Minutos)"
                type="number"
                min={1}
                max={60}
                value={String(settingsForm.min_delay_minutes)}
                onChange={(e) =>
                  setSettingsForm({
                    ...settingsForm,
                    min_delay_minutes: Number(e.target.value),
                  })
                }
                helperText="Pausa mínima entre envios"
              />
              <Input
                label="Espera Máxima (Minutos)"
                type="number"
                min={1}
                max={120}
                value={String(settingsForm.max_delay_minutes)}
                onChange={(e) =>
                  setSettingsForm({
                    ...settingsForm,
                    max_delay_minutes: Number(e.target.value),
                  })
                }
                helperText="Pausa máxima entre envios"
              />
            </div>
          </div>

          <div className="rounded-xl border border-surface-border bg-surface-subtle p-4">
            <Input
              label="Capacidade Máxima Diária (Disparos/dia)"
              type="number"
              min={10}
              max={1000}
              value={String(settingsForm.safe_capacity_daily)}
              onChange={(e) =>
                setSettingsForm({
                  ...settingsForm,
                  safe_capacity_daily: Number(e.target.value),
                })
              }
              helperText="Volume diário seguro de envios por chip"
            />
          </div>
        </div>
      </Drawer>

      {/* Drawer de Cadastro de Nova Fila */}
      <Drawer
        isOpen={isNewQueueDrawerOpen}
        onClose={() => setIsNewQueueDrawerOpen(false)}
        title="Nova Fila de Disparo"
        description="Configure uma esteira segmentada de ofertas com estratégia e grupos dedicados."
        width="md"
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setIsNewQueueDrawerOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="sm"
              isLoading={isSavingNewQueue}
              onClick={handleCreateQueue}
            >
              Criar Fila
            </Button>
          </div>
        }
      >
        <div className="space-y-4 p-5">
          <Input
            label="Nome da Fila"
            placeholder="Ex: Fila VIP Ticket Alto"
            value={newQueueForm.name}
            onChange={(e) =>
              setNewQueueForm({ ...newQueueForm, name: e.target.value })
            }
            helperText="Nome descritivo para identificar a esteira de envio"
          />

          <Input
            label="Slug (Identificador URL)"
            placeholder="Ex: ticket-alto"
            value={newQueueForm.slug}
            onChange={(e) =>
              setNewQueueForm({ ...newQueueForm, slug: e.target.value })
            }
            helperText="Opcional. Gerado automaticamente se deixado vazio"
          />

          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-content-main">
              Estratégia de Envio
            </label>
            <select
              value={newQueueForm.strategy_type}
              onChange={(e) =>
                setNewQueueForm({
                  ...newQueueForm,
                  strategy_type: e.target.value,
                })
              }
              className="focus:outline-hidden w-full rounded-xl border border-surface-border bg-surface-canvas px-3 py-2 text-xs text-content-main focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
            >
              <option value="daily_deals">
                Giro Diário (Cadência contínua com jitter)
              </option>
              <option value="high_ticket">
                Ticket Alto (Espaçamento estendido para produtos premium)
              </option>
              <option value="flash_sale">
                Flash Sale (Prioridade máxima e quarentena rápida)
              </option>
              <option value="mirror">
                Espelho VIP (Replicação segmentada para grupos VIP)
              </option>
              <option value="custom">Personalizada</option>
            </select>
          </div>

          <Input
            label="Prioridade de Agendamento"
            type="number"
            min={1}
            max={100}
            value={String(newQueueForm.priority ?? 1)}
            onChange={(e) =>
              setNewQueueForm({
                ...newQueueForm,
                priority: Number(e.target.value),
              })
            }
            helperText="Menor número = maior precedência no agendamento"
          />

          <div className="space-y-2">
            <label className="block text-xs font-semibold text-content-main">
              Grupos VIP de Destino (
              {newQueueForm.target_group_ids?.length ?? 0} selecionados)
            </label>
            <p className="text-micro text-content-muted">
              Ofertas enfileiradas nesta esteira herdam automaticamente estes
              grupos de destino se nenhum grupo for especificado na oferta.
            </p>
            <div className="max-h-48 space-y-1.5 overflow-y-auto rounded-xl border border-surface-border bg-surface-subtle p-3">
              {groups.length === 0 ? (
                <p className="py-2 text-center text-micro text-content-muted">
                  Nenhum grupo cadastrado no tenant.
                </p>
              ) : (
                groups.map((grp) => {
                  const isChecked =
                    newQueueForm.target_group_ids?.includes(grp.id) ?? false;
                  return (
                    <label
                      key={grp.id}
                      className="flex cursor-pointer items-center gap-2.5 rounded-lg p-1.5 text-xs transition-colors hover:bg-surface-panel"
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => {
                          const current = newQueueForm.target_group_ids || [];
                          const updated = isChecked
                            ? current.filter((id) => id !== grp.id)
                            : [...current, grp.id];
                          setNewQueueForm({
                            ...newQueueForm,
                            target_group_ids: updated,
                          });
                        }}
                        className="rounded border-surface-border"
                      />
                      <span className="font-medium text-content-main">
                        {grp.name}
                      </span>
                      {grp.status === 'active' ? (
                        <Badge variant="online">Ativo</Badge>
                      ) : (
                        <Badge variant="neutral">{grp.status}</Badge>
                      )}
                    </label>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </Drawer>

      {/* Drawer para Aplicação Rápida de Cupom */}
      {couponTargetQueueItem && (
        <ApplyCouponDrawer
          isOpen={Boolean(couponTargetQueueItem)}
          onClose={() => setCouponTargetQueueItem(null)}
          targetType="queue_item"
          targetId={couponTargetQueueItem.id}
          targetTitle={
            couponTargetQueueItem.offer_title || 'Item da Fila de Disparo'
          }
          currentPrice={couponTargetQueueItem.offer_promotional_price}
          originalPrice={couponTargetQueueItem.offer_original_price}
          onApplied={async () => {
            await loadData();
          }}
          applyFn={applyCouponToQueueItem}
        />
      )}

      {/* Drawer de Pacing Inteligente e Silêncio Noturno */}
      <PacingDrawer
        isOpen={isPacingDrawerOpen}
        onClose={() => setIsPacingDrawerOpen(false)}
        queues={dispatchQueues}
        selectedQueueId={selectedQueueId}
        onSave={handleSavePacingProfile}
      />
    </div>
  );
};
